import { Archive, ArrowCounterClockwise, FilePdf } from "@phosphor-icons/react";
import { Checkbox } from "components/ui/checkbox";
import { OpexKostenplaatsCell, OpexTicketCell } from "../components/OpexCells";
import { CellConfig } from "shared/components/UniversalTable/components";
import { formatCurrency } from "shared/utils/currency";

export interface TicketSuggestion {
  ticketId: number;
  title: string;
  /** Label van de kandidaat (bv. 'Sanitair'), zelfde chip als bij een gekoppeld ticket. */
  label?: string | null;
  score: number;
  reasons: string[];
  isExplicitReference: boolean;
}

export interface OpexInvoiceCellData {
  moveLineId: number;
  moveId?: number | null;
  moveName: string;
  date: string;
  description: string;
  partnerName: string;
  amountExclVat: number;
  kostenplaatsen?: Array<{ id: number; name: string; amount: number }>;
  buildings?: string[];
  hasPdf?: boolean;
  linkedTicket?: LinkedTicket | null;
  linkedTickets?: LinkedTicket[];
  /** Bewust niet te koppelen; staat in de archiefweergave. */
  isArchived?: boolean;
  archivedReason?: string | null;
}

/** Een gekoppeld helpdeskticket, met zijn label (bv. 'Sanitair'). */
export interface LinkedTicket {
  id: number;
  title: string;
  invoiceLineDbId?: number;
  label?: string | null;
}

interface CreateCellsParams {
  invoice: OpexInvoiceCellData;
  suggestions?: TicketSuggestion[];
  rowContext?: {
    isSelected: boolean;
    onToggleSelect: (selected: boolean) => void;
  };
  handlers: {
    onOpenPdf: () => void;
    onPickTicket: () => void;
    onAcceptSuggestion: (ticketId: number) => void;
    onUnlink: (invoiceLineDbId: number) => void;
    onArchive: () => void;
    onUnarchive: () => void;
  };
}

/**
 * Cellen voor één OPEX-factuurregel in de UniversalTable.
 *
 * De omschrijving staat vooraan en krijgt de meeste ruimte: dat is het veld waarop
 * een ticket herkend wordt (mediaan 30 tekens, dus het past op één regel).
 */
export const createOpexInvoiceCells = ({
  invoice,
  suggestions = [],
  rowContext,
  handlers,
}: CreateCellsParams): CellConfig[] => {
  const linkedTickets =
    invoice.linkedTickets ?? (invoice.linkedTicket ? [invoice.linkedTicket] : []);

  return [
    {
      id: "select",
      content: rowContext ? (
        <Checkbox
          checked={rowContext.isSelected}
          onCheckedChange={(checked) => rowContext.onToggleSelect(checked === true)}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Selecteer factuurregel ${invoice.moveName}`}
        />
      ) : null,
    },
    {
      id: "description",
      content: (
        <span
          className="text-neutral-primary block max-w-full truncate text-sm font-medium"
          title={invoice.description}
        >
          {invoice.description || "(geen omschrijving)"}
        </span>
      ),
    },
    {
      id: "partner",
      content: (
        <span className="text-neutral-primary block max-w-full truncate text-sm">
          {invoice.partnerName}
        </span>
      ),
    },
    {
      id: "date",
      content: (
        <span className="text-neutral-primary text-sm whitespace-nowrap">
          {new Date(invoice.date).toLocaleDateString("nl-BE")}
        </span>
      ),
    },
    {
      id: "kostenplaats",
      content: (
        <OpexKostenplaatsCell
          kostenplaatsen={invoice.kostenplaatsen}
          buildings={invoice.buildings}
        />
      ),
    },
    {
      id: "amount",
      align: "right",
      content: (
        <span className="text-neutral-primary text-sm font-semibold whitespace-nowrap">
          {formatCurrency(invoice.amountExclVat)}
        </span>
      ),
    },
    {
      id: "invoice",
      content: invoice.moveId ? (
        <a
          href={`https://portal.yally.be/odoo/vendor-bills/${invoice.moveId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-moss-primary block max-w-full truncate text-sm hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {invoice.moveName}
        </a>
      ) : (
        <span className="text-neutral-primary text-sm">{invoice.moveName}</span>
      ),
    },
    {
      id: "ticket",
      content: invoice.isArchived ? (
        <span
          className="bg-neutral-secondary-light text-neutral-secondary-mid inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
          title={invoice.archivedReason ?? "Gearchiveerd zonder reden"}
        >
          <Archive size={12} />
          {invoice.archivedReason || "Gearchiveerd"}
        </span>
      ) : (
        <OpexTicketCell
          linkedTickets={linkedTickets}
          suggestions={suggestions}
          onPickTicket={handlers.onPickTicket}
          onAcceptSuggestion={handlers.onAcceptSuggestion}
          onUnlink={handlers.onUnlink}
        />
      ),
    },
    {
      id: "pdf",
      align: "center",
      content: invoice.hasPdf ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handlers.onOpenPdf();
          }}
          aria-label="Bekijk PDF"
          title="Bekijk PDF"
          className="text-neutral-secondary-mid hover:text-moss-primary p-1"
        >
          <FilePdf size={16} />
        </button>
      ) : (
        <span className="text-neutral-secondary-mid text-xs">—</span>
      ),
    },
    {
      id: "actions",
      align: "center",
      content: invoice.isArchived ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handlers.onUnarchive();
          }}
          aria-label="Terug naar werkvoorraad"
          title="Terug naar werkvoorraad"
          className="text-neutral-secondary-mid hover:text-moss-primary p-1"
        >
          <ArrowCounterClockwise size={16} />
        </button>
      ) : linkedTickets.length === 0 ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handlers.onArchive();
          }}
          aria-label="Archiveer factuurregel"
          title="Hoort niet bij een ticket — archiveren"
          className="text-neutral-secondary-mid hover:text-brick-primary p-1"
        >
          <Archive size={16} />
        </button>
      ) : (
        <span className="text-neutral-secondary-mid text-xs">—</span>
      ),
    },
  ];
};
